import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CreateSponsor } from "../../api/CreateSponsor";
import { ModifySponsor } from "../../api/ModifySponsor";
import { SponsorSchema } from "../../data/validationSchemas/SponsorSchema";
import { z } from "zod";
import { normalizeOptionalUrl } from "../../logic/normalizeOptionalUrl";

import "../../styles/components/sponsor-modal.css";

const SponsorModal = ({
  isVisible,
  onClose,
  sponsor,
  gatheringID,
  fetchSponsorsData,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    description: "",
    isPriority: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (sponsor) {
      setFormData(sponsor);
    } else {
      setFormData({
        name: "",
        email: "",
        website: "",
        description: "",
        isPriority: false,
      });
    }
  }, [sponsor]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  const handleChange = (e, field) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const buildUpdatedData = () => {
    return {
      name: formData.name,
      email: formData.email,
      website: normalizeOptionalUrl(formData.website),
      description: formData.description,
      isPriority: formData.isPriority,
    };
  };

  const validateInputs = (dataToValidate) => {
    try {
      SponsorSchema.parse(dataToValidate);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors = error.errors.reduce((acc, err) => {
          acc[err.path[0]] = err.message;
          return acc;
        }, {});
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const updatedData = buildUpdatedData();

    if (validateInputs(updatedData)) {
      try {
        if (sponsor) {
          await ModifySponsor(gatheringID, sponsor.id, updatedData);
        } else {
          await CreateSponsor(updatedData, gatheringID);
        }

        await fetchSponsorsData();
        onClose();
      } catch (error) {
        console.error(error);
        alert("Error submitting sponsor data.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert("Please fix the errors in the form.");
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalMarkup = (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label={sponsor ? "Edit Sponsor" : "Add Sponsor"}
      >
        <button
          type="button"
          className="modal-close-btn"
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>

        <h2 className="sponsor-modal-h2">
          {sponsor ? "Edit Sponsor" : "Add Sponsor"}
        </h2>

        <div>
          <label className="sponsor-modal-label">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange(e, "name")}
            placeholder="Name"
            className="sponsor-modal-input"
          />
          {errors.name && <p className="error">{errors.name}</p>}
        </div>

        <div>
          <label className="sponsor-modal-label">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange(e, "email")}
            placeholder="Email"
            className="sponsor-modal-input"
          />
          {errors.email && <p className="error">{errors.email}</p>}
        </div>

        <div>
          <label className="sponsor-modal-label">Website</label>
          <input
            type="text"
            value={formData.website}
            onChange={(e) => handleChange(e, "website")}
            placeholder="Website, ex: google.com"
            className="sponsor-modal-input"
          />
          {errors.website && <p className="error">{errors.website}</p>}
        </div>

        <div>
          <label className="sponsor-modal-label">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange(e, "description")}
            placeholder="Description"
            className="sponsor-modal-input sponsor-description-input"
          />
          {errors.description && <p className="error">{errors.description}</p>}
        </div>

        <div className="checkbox-container">
          <label>
            Is this a Priority Sponsor? (Priority Sponsors are displayed more
            prominently in the app)
            <input
              type="checkbox"
              checked={formData.isPriority}
              onChange={(e) =>
                setFormData({ ...formData, isPriority: e.target.checked })
              }
            />
          </label>
        </div>

        <div className="sponsor-button-container">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="submit-button"
            type="button"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          <button onClick={onClose} className="submit-button" type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalMarkup, document.body);
};

export default SponsorModal;
